import { Anchor, Card, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { LoadingState, TimeZoneField, friendlyZoneName } from "@how/ui";
import { supabase } from "../../lib/supabase";

/** Words for a refusal from set_tenant_time_zone, as DCX support needs them. */
export function supportTimeZoneErrorMessage(error: { code?: string; message: string }): string {
  if (error.code === "23514") return error.message;
  if (error.code === "42501")
    return "Your support session no longer allows this. It may have ended, or it does not include settings.time_zone.";
  if (error.code === "P0002") return "This church was not found.";
  return "The time zone was not changed. Try again.";
}

export interface TimeZoneCardProps {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly timeZone: string;
}

/**
 * The church's time zone, for DCX support when a church calls (owner decision of 2026-10-03). Changeable only inside
 * an active read and write support session on this church that includes settings.time_zone, which the church
 * approves (or a second super admin, break-glass). A super admin has no standing right here either. Support says why;
 * the note goes into the church's audit log with the session.
 */
export function TimeZoneCard({ tenantId, tenantName, timeZone }: TimeZoneCardProps) {
  const queryClient = useQueryClient();
  const canChange = useQuery({
    queryKey: ["authorize", tenantId, "settings.time_zone", "write"],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc("authorize_check", {
        p_tenant_id: tenantId,
        p_permission_key: "settings.time_zone",
        p_write: true,
      });
      if (error) throw error;
      return data;
    },
  });

  const save = async (zone: string, reason: string): Promise<void> => {
    const { error } = await supabase.rpc("set_tenant_time_zone", {
      p_tenant_id: tenantId,
      p_time_zone: zone,
      p_reason: reason,
    });
    if (error) throw new Error(supportTimeZoneErrorMessage(error));
    notifications.show({
      color: "teal",
      title: "Time zone changed",
      message: `${tenantName} now runs on ${friendlyZoneName(zone)} time.`,
    });
    void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
  };

  return (
    <Card>
      <Stack gap="sm">
        <Title order={2} size="h4">
          Time zone
        </Title>
        {canChange.isPending ? (
          <LoadingState rows={2} />
        ) : (
          <TimeZoneField
            value={timeZone}
            canChange={canChange.data === true}
            requireReason
            churchName={tenantName}
            onSave={save}
            lockedMessage={
              <Text component="span" size="sm" c="dimmed">
                To change it, open a read and write{" "}
                <Anchor component={Link} to="/support" size="sm">
                  support session
                </Anchor>{" "}
                for this church that includes settings.time_zone. The church approves it, or a
                second super admin in an emergency.
              </Text>
            }
          />
        )}
      </Stack>
    </Card>
  );
}
